import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Users } from "lucide-react";
import { clearAuthData, storeAuthData, getRedirectPath } from "../../utils/authSession";
import "./GoogleAuthButton.css";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";
const BUILD_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let scriptPromise;
function loadGoogleScript() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.onload = resolve;
      s.onerror = () => { scriptPromise = null; reject(new Error("Could not load Google sign-in")); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

// The client ID comes from the build setting if there is one, otherwise from the backend (GOOGLE_CLIENT_ID there).
let configPromise;
function loadClientId() {
  if (BUILD_CLIENT_ID) return Promise.resolve(BUILD_CLIENT_ID);
  if (!configPromise) {
    configPromise = fetch(`${API_URL}/auth/google/config`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => d.client_id || null)
      .catch(() => { configPromise = null; return null; });
  }
  return configPromise;
}

/**
 * "Continue with Google" for both the sign-in and the sign-up page.
 * Google gives the browser a signed token; the backend verifies it. A first-time person is asked whether they are
 * a student or a teacher (the sign-up page can pass `role` so they are not asked twice).
 * Renders nothing when no client ID is configured, so the pages still work without it.
 */
export default function GoogleAuthButton({ role, remember = true, onError }) {
  const navigate = useNavigate();
  const holder = useRef(null);
  const roleRef = useRef(role);
  const [pending, setPending] = useState(null);   // { credential, email, name } waiting for a role
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [clientId, setClientId] = useState(BUILD_CLIENT_ID || null);

  useEffect(() => {
    let live = true;
    loadClientId().then((id) => { if (live) setClientId(id); });
    return () => { live = false; };
  }, []);

  roleRef.current = role;

  const fail = useCallback((text) => { setMessage(text); onError?.(text); }, [onError]);

  const send = useCallback(async (credential, chosenRole) => {
    setBusy(true);
    setMessage("");
    onError?.("");
    try {
      const res = await fetch(`${API_URL}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential, role: chosenRole || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Google sign-in failed. Please try again.");
      const data = body.data || {};
      if (data.needs_role) {
        setPending({ credential, email: data.email, name: data.name });
        return;
      }
      if (!data.access_token) throw new Error("Invalid response from server");
      clearAuthData();
      if (!storeAuthData(data, remember)) throw new Error("Failed to store authentication data");
      navigate(getRedirectPath(data.role), { replace: true });
    } catch (err) {
      fail(err.message === "Failed to fetch" ? "Network error. Please check your connection and try again." : err.message);
    } finally {
      setBusy(false);
    }
  }, [navigate, remember, fail, onError]);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    loadGoogleScript()
      .then(() => {
        if (cancelled || !holder.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (resp) => send(resp.credential, roleRef.current),
        });
        holder.current.innerHTML = "";
        window.google.accounts.id.renderButton(holder.current, {
          theme: "outline", size: "large", text: "continue_with", shape: "rectangular", width: Math.min(holder.current.clientWidth || 320, 400),
        });
      })
      .catch(() => fail("Google sign-in is unavailable right now. Please use your email instead."));
    return () => { cancelled = true; };
  }, [send, fail, clientId, pending]);

  if (!clientId) return null;

  return (
    <div className="gauth">
      <div className="gauth-divider"><span>or</span></div>

      {pending ? (
        <div className="gauth-role" role="group" aria-label="Choose your role">
          <p className="gauth-role-title">Welcome{pending.name ? `, ${pending.name}` : ""}! One last step: I am a…</p>
          <p className="gauth-role-sub">{pending.email}</p>
          <div className="gauth-role-row">
            <button type="button" disabled={busy} onClick={() => send(pending.credential, "student")}>
              <GraduationCap size={18} aria-hidden="true" /> Student
            </button>
            <button type="button" disabled={busy} onClick={() => send(pending.credential, "teacher")}>
              <Users size={18} aria-hidden="true" /> Teacher
            </button>
          </div>
          <button type="button" className="gauth-cancel" onClick={() => setPending(null)}>Cancel</button>
        </div>
      ) : (
        <div ref={holder} className={`gauth-button ${busy ? "busy" : ""}`} aria-busy={busy} />
      )}

      {message && !onError && <p className="gauth-error" role="alert">{message}</p>}
    </div>
  );
}
